import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import moment from "moment-timezone";
import Styles from "./Weather.module.scss";

type RadarFrame = { time: number; path: string };
type RainViewerMaps = {
  host: string;
  radar?: { past?: RadarFrame[]; nowcast?: RadarFrame[] };
};

const RAINVIEWER_CATALOG = "https://api.rainviewer.com/public/weather-maps.json";
const FRAME_MS = 600; // animation speed between radar frames
// RainViewer tile template params: 256px tiles, color scheme 2 (Universal Blue),
// smoothed (1), with snow colors (1). https://www.rainviewer.com/api.html
const tileUrl = (host: string, path: string) => `${host}${path}/256/{z}/{x}/{y}/2/1_1.png`;

interface RadarMapProps {
  lat: number;
  lon: number;
  timezone: string;
}

export const RadarMap = ({ lat, lon, timezone }: RadarMapProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [frameTime, setFrameTime] = useState<number | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const map = L.map(containerRef.current, {
      center: [lat, lon],
      zoom: 8,
      zoomControl: true,
      attributionControl: true,
      scrollWheelZoom: false,
    });

    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png", {
      subdomains: "abcd",
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; ' +
        '<a href="https://carto.com/attributions">CARTO</a> · Radar <a href="https://www.rainviewer.com/">RainViewer</a>',
    }).addTo(map);

    // Mark the venue. circleMarker avoids Leaflet's default image-icon path
    // breakage under webpack/CRA (no asset imports needed).
    L.circleMarker([lat, lon], {
      radius: 7,
      color: "#ffffff",
      weight: 2,
      fillColor: "#1d4ed8",
      fillOpacity: 1,
    }).addTo(map);

    // Leaflet can mis-measure when the container animates in; nudge it.
    const sizeTimer = window.setTimeout(() => map.invalidateSize(), 200);

    let radarLayer: L.TileLayer | null = null;
    let cycleTimer: number | undefined;
    let cancelled = false;

    fetch(RAINVIEWER_CATALOG)
      .then((res) => res.json() as Promise<RainViewerMaps>)
      .then((data) => {
        if (cancelled || !data?.host) return;
        const frames = [...(data.radar?.past ?? []), ...(data.radar?.nowcast ?? [])];
        if (frames.length === 0) return;

        let i = frames.length - 1; // start on the most recent observed frame
        const show = (idx: number) => {
          const frame = frames[idx];
          const url = tileUrl(data.host, frame.path);
          if (radarLayer) {
            radarLayer.setUrl(url);
          } else {
            // RainViewer radar tiles only exist up to zoom 7; beyond that the
            // service returns a "Zoom Level Not Supported" placeholder, so cap
            // maxNativeZoom and let Leaflet upscale the z7 tiles.
            radarLayer = L.tileLayer(url, { opacity: 0.6, zIndex: 5, maxNativeZoom: 7 }).addTo(map);
          }
          setFrameTime(frame.time * 1000);
        };
        show(i);
        cycleTimer = window.setInterval(() => {
          i = (i + 1) % frames.length;
          show(i);
        }, FRAME_MS);
      })
      .catch(() => {
        /* radar overlay is best-effort; base map still renders */
      });

    return () => {
      cancelled = true;
      window.clearTimeout(sizeTimer);
      if (cycleTimer) window.clearInterval(cycleTimer);
      map.remove();
    };
  }, [lat, lon]);

  return (
    <div className={Styles.radarWrap}>
      <div ref={containerRef} className={Styles.radarMap} />
      {frameTime != null && (
        <div className={Styles.radarTime}>
          Radar · {moment.tz(frameTime, timezone || moment.tz.guess()).format("h:mm A")}
        </div>
      )}
    </div>
  );
};
